/*
  assets/gcitypartners-review-brief-schema.js — GCITYPARTNERS-SAFE-LOCAL-DRAFT-08
  Canonical owner: Screen 05 local-only Project Brief runtime.

  Scope:
  - one [data-enterprise-triage-form] inside each [data-gcitypartners-page-system] root
  - safe context hydration, matching local-draft restore/update and explicit Contact handoff
  - desired outcome is stored with the same local-only, non-contact brief contract

  Boundary:
  - no contact persistence, PII/description in URL, backend submission, checkout,
    GFun mutation, file upload, valuation/survey conclusion or formal engagement
*/
(function (window, document) {
  'use strict';

  var VERSION = '1.15.2-brief-owner-12';
  if (
    window.GCityPartnersReviewBriefSchema &&
    window.GCityPartnersReviewBriefSchema.version === VERSION
  ) {
    window.GCityPartnersReviewBriefSchema.bind(document);
    return;
  }

  var STORAGE_KEY = 'gcitypartners_review_brief_draft_v1';
  var UAV_HANDOFF_STORAGE_KEY = 'gcity_uav_precheck_handoff_v1';
  var ROOT_SELECTOR = '[data-gcitypartners-page-system]';
  var FORM_SELECTOR = '[data-enterprise-triage-form]';
  var MAX_RECORDS = 24;
  var CARD_KEYS = [
    'littleg_city_mission_cards',
    'littleg_city_mission_cards_v1',
    'littleg_offline_city_missions',
    'littleg_result_history',
    'littleg.cityMission.lastResult',
    'littleg_city_plus_one_history_v1',
    'drg_city_mission_history_v1',
  ];
  var FIELDS = [
    'source',
    'intent',
    'lead_type',
    'next_destination',
    'record_id',
    'result_id',
    'scene',
    'service_lane',
    'business_line',
    'business_line_label',
    'business_line_canonical_lane',
    'project_location',
    'project_stage',
    'business_need',
    'observation',
    'desired_outcome',
    'evidence_files',
    'precheck_type',
    'precheck_version',
    'precheck_score',
    'precheck_purpose',
    'precheck_evidence',
    'precheck_recommendations',
    'asset_access_link',
    'gsd_requirement',
    'project_timeline',
    'budget_range',
    'csdi_location_name',
    'csdi_location_address',
    'csdi_location_district',
    'csdi_location_x',
    'csdi_location_y',
    'csdi_source',
    'source_context',
    'source_card_id',
    'source_card_type',
    'resource_mode',
    'resource_type',
    'resource_goals',
    'card_context_found',
    'card_context_source',
    'review_scope',
    'privacy_scope',
    'formal_submission_status',
    'review_brief_status',
  ];

  /** @type {Set<DirectoryRoot>} */
  const mountedRoots = new Set();
  var stateByRoot = new WeakMap();

  /**
   * @param {unknown} value
   * @returns {value is ProjectRecord}
   */
  function isProjectRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }
  /** @param {unknown} value */
  function clean(value) {
    return String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim();
  }

  function clip(value, max) {
    return clean(value).slice(0, max || 500);
  }

  function opaque(value) {
    var text = clean(value);
    return /^[A-Za-z0-9._:-]{1,160}$/.test(text) ? text : '';
  }

  function laneValue(value) {
    return clean(value)
      .toLowerCase()
      .replace(/_/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .slice(0, 120);
  }

  function coordinate(value) {
    var text = clean(value);
    if (!text || !/^-?\d+(?:\.\d+)?$/.test(text)) return '';
    var number = Number(text);
    return Number.isFinite(number) ? String(number).slice(0, 60) : '';
  }

  function readinessScore(value) {
    var number = Number(value);
    if (!Number.isFinite(number)) return '';
    return String(Math.max(0, Math.min(100, Math.round(number))));
  }

  function safeAccessUrl(value) {
    var text = clean(value);
    if (!text) return '';
    try {
      var url = new URL(text);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      return url.toString().slice(0, 600);
    } catch (_error) {
      return '';
    }
  }

  function firstExisting() {
    for (var index = 0; index < arguments.length; index += 1) {
      if (clean(arguments[index])) return arguments[index];
    }
    return '';
  }

  function parse(raw, fallback) {
    try {
      var parsed = raw ? JSON.parse(raw) : fallback;
      return parsed == null ? fallback : parsed;
    } catch (_error) {
      return fallback;
    }
  }

  function asArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value !== 'object') return [];
    if (Array.isArray(value.items)) return value.items;
    if (Array.isArray(value.cards)) return value.cards;
    if (Array.isArray(value.results)) return value.results;
    if (Array.isArray(value.history)) return value.history;
    return [value];
  }

  function storageValue(storage, key) {
    if (!storage) return '';
    try {
      return storage.getItem(key) || '';
    } catch (_error) {
      return '';
    }
  }

  function readableStorage(name) {
    try {
      return window[name];
    } catch (_error) {
      return null;
    }
  }

  function readAllRaw(key) {
    var localValue = storageValue(readableStorage('localStorage'), key);
    var sessionValue = storageValue(readableStorage('sessionStorage'), key);
    var values = [];
    if (localValue) values.push(localValue);
    if (sessionValue && sessionValue !== localValue) values.push(sessionValue);
    return values;
  }

  function writeRaw(key, value) {
    var serialized = JSON.stringify(value);

    try {
      window.localStorage.setItem(key, serialized);
      try {
        window.sessionStorage.removeItem(key);
      } catch (_error) {}
      return 'localStorage';
    } catch (_error) {}

    try {
      window.sessionStorage.setItem(key, serialized);
      return 'sessionStorage';
    } catch (_error) {
      return '';
    }
  }

  function stableHash(value) {
    var text = String(value || '');
    var hash = 0;
    for (var index = 0; index < text.length; index += 1) {
      hash = (hash << 5) - hash + text.charCodeAt(index);
      hash |= 0;
    }
    return Math.abs(hash).toString(36);
  }

  /* 💎 多語系精準比對 (zh-CN / zh-HK / zh-TW / en) */
  function localeFamily() {
    var lang = (document.documentElement.lang || '').toLowerCase();
    var path = window.location.pathname.toLowerCase();
    if (lang.indexOf('zh-cn') > -1 || lang.indexOf('zh-hans') > -1 || /\/zh-cn(?:\/|$)/.test(path))
      return 'zh-CN';
    if (lang.indexOf('zh-tw') > -1 || /\/zh-tw(?:\/|$)/.test(path)) return 'zh-TW';
    if (lang.indexOf('zh-hk') > -1 || lang.indexOf('zh-hant') > -1 || /\/zh-hk(?:\/|$)/.test(path))
      return 'zh-HK';
    if (/^zh/i.test(lang)) return 'zh-HK';
    return 'en';
  }

  function words(cn, hk, tw, en) {
    var family = localeFamily();
    if (family === 'zh-CN') return cn;
    if (family === 'zh-TW') return tw || hk;
    if (family === 'zh-HK') return hk;
    return en;
  }

  function localePrefix() {
    var match = clean(window.location.pathname).match(/^\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*(?=\/)/i);
    return match ? match[0] : '';
  }

  function localizedPagePath(path) {
    var prefix = localePrefix();
    if (!prefix || path.indexOf(prefix + '/') === 0) return path;
    return prefix + path;
  }

  function searchParams() {
    try {
      return new URLSearchParams(window.location.search || '');
    } catch (_error) {
      return new URLSearchParams();
    }
  }

  function param(search, key) {
    return clip(search.get(key) || '', 180);
  }

  function field(form, name) {
    var node = form && form.elements && form.elements.namedItem(name);
    return node ? clean(node.value) : '';
  }

  function setField(form, name, value, overwrite) {
    var node = form && form.elements && form.elements.namedItem(name);
    if (!node || !clean(value)) return;
    if (overwrite || !clean(node.value)) node.value = String(value);
  }

  function optionLabel(select) {
    if (!select || select.selectedIndex < 0) return '';
    return clean(
      select.options[select.selectedIndex] && select.options[select.selectedIndex].textContent
    );
  }

  function cardId(card) {
    var source = card && card.payload ? Object.assign({}, card.payload, card) : card || {};

    return opaque(
      firstExisting(
        source.record_id,
        source.recordId,
        source.result_id,
        source.resultId,
        source.card_id,
        source.cardId,
        source.id,
        source.evidence_id,
        source.evidenceId
      )
    );
  }

  function findCard(id) {
    var safeId = opaque(id);
    if (!safeId) return null;

    for (var keyIndex = 0; keyIndex < CARD_KEYS.length; keyIndex += 1) {
      var rawValues = readAllRaw(CARD_KEYS[keyIndex]);
      for (var valueIndex = 0; valueIndex < rawValues.length; valueIndex += 1) {
        var rows = asArray(parse(rawValues[valueIndex], null));
        for (var rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
          if (cardId(rows[rowIndex]) !== safeId) continue;
          var card =
            rows[rowIndex] && rows[rowIndex].payload
              ? Object.assign({}, rows[rowIndex].payload, rows[rowIndex])
              : rows[rowIndex];
          card.__source_key = CARD_KEYS[keyIndex];
          return card;
        }
      }
    }

    return null;
  }

  function cardDescription(card) {
    if (!card) return '';

    var clues = asArray(card.clues || card.selected_clue_labels || card.clue_labels)
      .map(function (item) {
        return typeof item === 'string'
          ? clean(item)
          : clean(item && (item.label || item.title || item.name || item.text));
      })
      .filter(Boolean)
      .slice(0, 3);

    return clip(
      firstExisting(
        card.informal_summary,
        card.memory_note,
        card.observation,
        card.issue_guess,
        card.problem,
        card.share_text,
        [firstExisting(card.place_label, card.location, card.district), clues.join('｜')]
          .filter(Boolean)
          .join('｜')
      ),
      1200
    );
  }

  function businessNeedFor(lane) {
    var key = laneValue(lane);
    if (/property|valuation|asset/.test(key)) return 'property-spatial-brief';
    if (/infrastructure|digital|survey|spatial/.test(key)) return 'spatial-survey-report';
    return 'enterprise-brief';
  }

  function laneForNeed(need, fallback) {
    if (need === 'property-spatial-brief') return 'property-assets-investment';
    if (need === 'spatial-survey-report') {
      var safeFallback = laneValue(fallback);
      return safeFallback && safeFallback !== 'urban-renewal'
        ? safeFallback
        : 'infrastructure-engineering';
    }
    if (need === 'tender-radar') return 'digital-city';
    if (need === 'low-altitude-scenario-check') {
      return laneValue(fallback) || 'digital-city';
    }
    return laneValue(fallback) || 'urban-renewal';
  }

  const workloadStateByForm = new WeakMap();
  const partnerStateByForm = new WeakMap();

  function normalizeCapabilityAssessments(value) {
    if (!Array.isArray(value) || value.length > 3) return [];

    const capabilities = ['spatial-data', 'mission-management', 'governance-events'];
    const states = ['unknown', 'self', 'partner', 'self_partner'];
    const seen = new Set();
    const rows = [];

    for (const row of value) {
      if (
        !row ||
        !capabilities.includes(row.capability) ||
        !states.includes(row.state) ||
        row.verified !== false ||
        seen.has(row.capability) ||
        row.basis !== (row.state === 'unknown' ? 'not_confirmed' : 'user_self_report')
      )
        return [];

      seen.add(row.capability);
      rows.push({
        capability: row.capability,
        state: row.state,
        basis: row.state === 'unknown' ? 'not_confirmed' : 'user_self_report',
        verified: false,
      });
    }
    return rows;
  }

  function partnerRequestsFromAssessments(value) {
    return normalizeCapabilityAssessments(value)
      .filter((row) => row.state === 'partner' || row.state === 'self_partner')
      .map((row) => ({
        capability: row.capability,
        reason: row.state === 'partner' ? 'support_needed' : 'voluntary_collaboration',
        scope_status: 'not_confirmed',
        basis: 'user_self_report',
        verified: false,
      }));
  }

  function capabilityGapsFromAssessments(value) {
    return normalizeCapabilityAssessments(value)
      .filter((row) => row.state === 'partner')
      .map((row) => ({
        capability: row.capability,
        status: 'partner_requested',
        basis: 'user_self_report',
        verified: false,
      }));
  }

  function renderPartnerSummary(form, value, stale) {
    const node = form.querySelector('[data-gcp-partner-summary]');
    if (!node) return;

    if (stale) {
      node.hidden = false;
      node.textContent = words(
        '需求正文已修改，本次保存不再自动关联原合作自评。文字仍保留，请重新确认合作范围。',
        '需求正文已修改，本次儲存不再自動關聯原合作自評。文字仍保留，請重新確認合作範圍。',
        '需求正文已修改，本次儲存不再自動關聯原合作自評。文字仍保留，請重新確認合作範圍。',
        'The brief text changed. This save no longer associates the original collaboration assessment. Your text remains; reconfirm the partner scope.'
      );
      return;
    }

    const requests = partnerRequestsFromAssessments(value);
    if (!requests.length) {
      node.hidden = true;
      node.textContent = '';
      return;
    }

    const labels = {
      'spatial-data': words('三维空间数据', '三維空間資料', '三維空間資料', '3D spatial data'),
      'mission-management': words(
        '任务空间管理',
        '任務空間管理',
        '任務空間管理',
        'Mission spatial management'
      ),
      'governance-events': words(
        '治理事件数据',
        '治理事件資料',
        '治理事件資料',
        'Governance event data'
      ),
    };

    const lines = requests.map(
      (row) =>
        labels[row.capability] +
        '：' +
        (row.reason === 'support_needed'
          ? words('需要伙伴补充', '需要伙伴補充', '需要伙伴補充', 'Partner support requested')
          : words(
              '可覆盖，仍希望合作',
              '可覆蓋，仍希望合作',
              '可覆蓋，仍希望合作',
              'Coverage reported; collaboration requested'
            ))
    );

    node.hidden = false;
    node.textContent = [
      words(
        '已带入的合作意向（未经核验）',
        '已帶入的合作意向（未經核驗）',
        '已帶入的合作意向（未經核驗）',
        'Imported collaboration interests (unverified)'
      ),
      ...lines,
      words(
        '合作范围待确认；保存不会创建或分发正式 RFQ。',
        '合作範圍待確認；儲存不會建立或分發正式 RFQ。',
        '合作範圍待確認；儲存不會建立或分發正式 RFQ。',
        'Partner scope remains unconfirmed. Saving does not create or distribute a formal RFQ.'
      ),
    ].join('\n');
  }

  function partnerAssessmentsForForm(form) {
    const state = partnerStateByForm.get(form);
    if (!state) return [];

    if (state.observation !== clean(field(form, 'description'))) {
      renderPartnerSummary(form, [], true);
      return [];
    }
    return normalizeCapabilityAssessments(state.assessments);
  }

  function normalizeWorkloadEstimate(value) {
    if (
      !value ||
      value.version !== 1 ||
      value.formula_version !== 'monthly-task-hours-v1' ||
      value.period !== 'monthly' ||
      value.basis !== 'user_inputs' ||
      value.verified !== false ||
      !Number.isFinite(value.created_at) ||
      value.created_at <= 0 ||
      !Array.isArray(value.tasks) ||
      value.tasks.length < 1 ||
      value.tasks.length > 2
    )
      return null;

    const seen = new Set();
    const tasks = [];

    function validNumber(number, min, max, integer) {
      return (
        typeof number === 'number' &&
        Number.isFinite(number) &&
        number >= min &&
        number <= max &&
        (!integer || Number.isInteger(number))
      );
    }

    function matches(number, expected) {
      return (
        typeof number === 'number' &&
        Number.isFinite(number) &&
        Math.abs(number - expected) <= 1e-8 * Math.max(1, Math.abs(expected))
      );
    }

    for (const row of value.tasks) {
      if (
        !row ||
        !['inspection', 'event_review'].includes(row.task) ||
        seen.has(row.task) ||
        !row.inputs
      )
        return null;

      seen.add(row.task);
      const inputs = row.inputs;
      let required;
      let visits;
      let safeInputs;

      if (
        !validNumber(inputs.minutes, 0.1, 100000, false) ||
        !validNumber(inputs.available_hours, 0, 1000000, false)
      )
        return null;

      if (row.task === 'inspection') {
        if (
          !validNumber(inputs.points, 1, 1000000, true) ||
          !validNumber(inputs.frequency, 1, 10000, true)
        )
          return null;

        visits = inputs.points * inputs.frequency;
        required = (visits * inputs.minutes) / 60;
        if (!matches(row.visits_per_month, visits)) return null;

        safeInputs = {
          points: inputs.points,
          frequency: inputs.frequency,
          minutes: inputs.minutes,
          available_hours: inputs.available_hours,
        };
      } else {
        if (!validNumber(inputs.events, 0, 1000000, true)) return null;
        required = (inputs.events * inputs.minutes) / 60;
        safeInputs = {
          events: inputs.events,
          minutes: inputs.minutes,
          available_hours: inputs.available_hours,
        };
      }

      const gap = Math.max(0, required - inputs.available_hours);
      if (
        !matches(row.required_hours, required) ||
        !matches(row.available_hours, inputs.available_hours) ||
        !matches(row.gap_hours, gap)
      )
        return null;

      const task = {
        task: row.task,
        inputs: safeInputs,
        required_hours: required,
        available_hours: inputs.available_hours,
        gap_hours: gap,
      };
      if (row.task === 'inspection') task.visits_per_month = visits;
      tasks.push(task);
    }

    return {
      version: 1,
      formula_version: 'monthly-task-hours-v1',
      period: 'monthly',
      basis: 'user_inputs',
      verified: false,
      created_at: value.created_at,
      tasks,
      exclusions: [
        'travel',
        'preparation',
        'weather_delays',
        'rework',
        'exceptions',
        'permit_related_work',
      ],
    };
  }

  function workloadForForm(form) {
    const state = workloadStateByForm.get(form);
    if (!state || state.observation !== clean(field(form, 'description'))) return null;

    return normalizeWorkloadEstimate(state.estimate);
  }

  /** @param {unknown} value */
  /**
   * @param {Element} node
   * @returns {node is HTMLElement}
   */
  function isHtmlElement(node) {
    return node instanceof HTMLElement;
  }

  /** @param {unknown} value */
  function normalize(value) {
    var source = value && typeof value === 'object' ? value : {};
    var recordId = opaque(source.record_id);
    var resultId = opaque(source.result_id);
    if (resultId === recordId) resultId = '';

    var createdAt = Number(source.created_at);
    if (!Number.isFinite(createdAt) || createdAt <= 0) createdAt = Date.now();
    var updatedAt = Number(source.updated_at);
    if (!Number.isFinite(updatedAt) || updatedAt <= 0) updatedAt = createdAt;

    return {
      source: clip(source.source, 120) || 'gcitypartners-gateway',
      intent: 'review-brief',
      lead_type: clip(source.lead_type, 80) || 'city_issue',
      next_destination: 'gcitypartners',
      record_id: recordId,
      result_id: resultId,
      scene: laneValue(source.scene),
      service_lane: laneValue(source.service_lane),
      business_line: clip(source.business_line, 120),
      business_line_label: clip(source.business_line_label, 180),
      business_line_canonical_lane: laneValue(
        source.business_line_canonical_lane || source.service_lane
      ),
      project_location: clip(source.project_location, 240),
      project_stage: clip(source.project_stage, 80),
      business_need: clip(source.business_need, 120),
      observation: clip(source.observation, 1200),
      workload_estimate: normalizeWorkloadEstimate(source.workload_estimate),
      capability_assessments: normalizeCapabilityAssessments(source.capability_assessments),
      capability_gaps: capabilityGapsFromAssessments(source.capability_assessments),
      partner_requests: partnerRequestsFromAssessments(source.capability_assessments),
      desired_outcome: clip(source.desired_outcome, 800),
      evidence_files: clip(source.evidence_files, 800),
      precheck_type: clip(source.precheck_type, 80),
      precheck_version: clip(source.precheck_version, 80),
      precheck_score: readinessScore(source.precheck_score),
      precheck_purpose: clip(source.precheck_purpose, 120),
      precheck_evidence: clip(source.precheck_evidence, 600),
      precheck_recommendations: clip(source.precheck_recommendations, 400),
      asset_access_link: safeAccessUrl(source.asset_access_link),
      gsd_requirement: clip(source.gsd_requirement, 80),
      project_timeline: clip(source.project_timeline, 80),
      budget_range: clip(source.budget_range, 80),
      csdi_location_name: clip(source.csdi_location_name, 240),
      csdi_location_address: clip(source.csdi_location_address, 320),
      csdi_location_district: clip(source.csdi_location_district, 120),
      csdi_location_x: coordinate(source.csdi_location_x),
      csdi_location_y: coordinate(source.csdi_location_y),
      csdi_source: clip(source.csdi_source, 160),
      source_context: clip(source.source_context, 120) || 'manual-or-safe-url-context',

      source_card_id: opaque(source.source_card_id),

      source_card_type: clip(source.source_card_type, 80) || 'manual_city_issue',

      resource_mode:
        ['need', 'provide'].indexOf(clean(source.resource_mode)) > -1
          ? clean(source.resource_mode)
          : '',

      resource_type:
        ['qualification', 'people', 'technology', 'opportunity'].indexOf(
          clean(source.resource_type)
        ) > -1
          ? clean(source.resource_type)
          : '',

      resource_goals: clip(source.resource_goals, 320),

      card_context_found: source.card_context_found === true,
      card_context_source: clip(source.card_context_source, 120) || 'none',
      review_scope: 'informal-preliminary-summary-local-draft',
      privacy_scope: 'local_only_no_contact_saved',
      formal_submission_status: 'not_submitted',
      review_brief_status: 'local_saved',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      contact_note: '',
      backend_submission: false,
      checkout_enabled: false,
      formal_professional_conclusion: false,
      valuation_conclusion: false,
      surveying_conclusion: false,
      updated_at: updatedAt,
      created_at: createdAt,
    };
  }

  function readDrafts() {
    var byRecordId = new Map();

    readAllRaw(STORAGE_KEY).forEach(function (raw) {
      asArray(parse(raw, []))
        .map(normalize)
        .filter(function (draft) {
          return Boolean(draft.record_id);
        })
        .forEach(function (draft) {
          var current = byRecordId.get(draft.record_id);
          if (!current || draft.updated_at > current.updated_at) {
            byRecordId.set(draft.record_id, draft);
          }
        });
    });

    return Array.from(byRecordId.values())
      .sort(function (left, right) {
        return right.updated_at - left.updated_at;
      })
      .slice(0, MAX_RECORDS);
  }

  function findDraft(recordId) {
    var safeId = opaque(recordId);
    if (!safeId) return null;
    return (
      readDrafts().find(function (draft) {
        return draft.record_id === safeId;
      }) || null
    );
  }

  function findDraftForContext(recordId, resultId) {
    var safeRecordId = opaque(recordId);
    var safeResultId = opaque(resultId);

    return (
      readDrafts().find(function (draft) {
        return (
          (safeRecordId && draft.record_id === safeRecordId) ||
          (safeResultId && draft.result_id === safeResultId)
        );
      }) || null
    );
  }

  function save(draft) {
    var nextDraft = normalize(
      Object.assign({}, draft, {
        updated_at: Date.now(),
      })
    );

    if (!nextDraft.record_id) throw new Error('record_id_missing');

    var rows = readDrafts();
    var existing = rows.find(function (row) {
      return row.record_id === nextDraft.record_id;
    });
    if (existing) {
      if (existing.created_at) nextDraft.created_at = existing.created_at;
      // Keep the latest draft authoritative across storage fallback and clock rollback.
      nextDraft.updated_at = Math.max(nextDraft.updated_at, existing.updated_at + 1);
    }

    var nextRows = [nextDraft]
      .concat(
        rows.filter(function (row) {
          return row.record_id !== nextDraft.record_id;
        })
      )
      .slice(0, MAX_RECORDS);
    var storage = writeRaw(STORAGE_KEY, nextRows);

    if (!storage) throw new Error('storage_unavailable');
    return { draft: nextDraft, storage: storage, count: nextRows.length };
  }

  function setStatus(root, form, message) {
    var status = form && form.querySelector('[data-gcp-review-status]');
    if (!status && root) status = root.querySelector('[data-gcp-review-status]');
    if (status) status.textContent = message;
  }

  function contactUrl(draft) {
    var query = new URLSearchParams({
      source: 'gcitypartners-review-brief',
      intent: 'professional-review',
    });
    if (opaque(draft.record_id)) query.set('record_id', draft.record_id);
    if (laneValue(draft.service_lane)) query.set('service_lane', laneValue(draft.service_lane));

    const inlineContact = document.querySelector('[data-contact-root]');
    if (inlineContact) return '#contact-portal-form';

    return localizedPagePath('/pages/contact') + '?' + query.toString() + '#contact-portal-form';
  }

  /* 💎 建立「聯絡 GLG」按鈕，並套用篩選 gcp-btn--secondary 白水晶膠囊類別 */
  function ensureContactLink(form, draft) {
    var actions = form.querySelector('.gcp-actions');
    if (!actions) return;

    var link = actions.querySelector('[data-gcp-safe-contact-handoff]');
    if (!link) {
      link = document.createElement('a');
      link.className = 'gcp-btn gcp-btn--secondary';
      link.setAttribute('data-gcp-safe-contact-handoff', '');
      link.setAttribute('data-no-legacy-normalize', 'true');
      link.setAttribute(
        'data-destination-owner',
        'sections/support-form.liquid#contact-portal-form'
      );
      actions.appendChild(link);
    }

    link.classList.toggle('lg-form__submit', form.classList.contains('lg-form'));
    var importedUav = draft && draft.precheck_type === 'uav-3d';
    const inlineContact = document.querySelector('[data-contact-root]');
    link.textContent = inlineContact
      ? words(
          '继续填写联系资料 →',
          '繼續填寫聯絡資料 →',
          '繼續填寫聯絡資料 →',
          'Continue to contact details →'
        )
      : importedUav
        ? words(
            '继续正式项目提交 →',
            '繼續正式項目提交 →',
            '繼續正式專案提交 →',
            'Continue to project submission →'
          )
        : words('联系 GLG', '聯絡 GLG', '聯絡 GLG', 'Contact GLG');
    link.href = contactUrl(draft);
    link.hidden = importedUav && !clean(draft.project_location);
  }

  function writeRecordIdToUrl(recordId) {
    var safeId = opaque(recordId);
    if (!safeId) return;

    try {
      var url = new URL(window.location.href);
      url.searchParams.set('record_id', safeId);
      url.searchParams.delete('result_id');
      url.hash = 'gcp-brief-form';
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (_error) {}
  }

  function reducedMotion() {
    try {
      return Boolean(
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    } catch (_error) {
      return false;
    }
  }

  /* Canonical Context Bar is the only top-offset shell surface. */
  function getHeaderOffset() {
    var contextBar = document.querySelector('[data-shell-surface="context-bar"]');
    return (contextBar ? contextBar.offsetHeight : 0) + 16;
  }

  function scrollToElement(element) {
    if (!element) return;
    var rect = element.getBoundingClientRect();
    var targetY = window.pageYOffset + rect.top - getHeaderOffset();
    try {
      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    } catch (_error) {
      element.scrollIntoView();
    }
  }

  function bridgeLegacyHash(form, state) {
    if (window.location.hash !== '#review-brief') return;

    var section = form.closest('section') || form;
    try {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search + '#gcp-brief-form'
      );
    } catch (_error) {}

    state.scrollTimer = window.setTimeout(function () {
      state.scrollTimer = null;
      if (!state.active || !section.isConnected) return;
      scrollToElement(section);
    }, 0);
  }

  function readResourceSharingHandoff() {
    var raw = storageValue(
      readableStorage('sessionStorage'),
      'gcp_resource_sharing_brief_context_v1'
    );

    if (!raw) return null;

    var value = parse(raw, null);

    if (!value || typeof value !== 'object') {
      return null;
    }

    try {
      window.sessionStorage.removeItem('gcp_resource_sharing_brief_context_v1');
    } catch (_error) {}

    return value;
  }

  function readUavPrecheckHandoff(search) {
    if (param(search, 'precheck') !== 'uav-3d') return null;

    var storage;
    try {
      storage = window.sessionStorage;
    } catch (_error) {
      return null;
    }
    var value = parse(storageValue(storage, UAV_HANDOFF_STORAGE_KEY), null);

    if (!value || typeof value !== 'object') return null;
    if (clean(value.precheck_type) !== 'uav-3d') return null;
    if (clean(value.precheck_version) !== 'uav-readiness-v1') return null;

    var createdAt = Number(value.created_at);
    if (
      !Number.isFinite(createdAt) ||
      createdAt > Date.now() ||
      Date.now() - createdAt > 30 * 60 * 1000
    ) {
      try {
        window.sessionStorage.removeItem(UAV_HANDOFF_STORAGE_KEY);
      } catch (_error) {}
      return null;
    }

    return {
      source: 'uav-precheck',
      source_context: 'uav-3d-precheck',
      source_card_type: 'uav_3d_precheck',
      service_lane: laneValue(value.service_lane) || 'infrastructure-engineering',
      business_need: 'low-altitude-scenario-check',
      project_stage: 'pre-project-preparation',
      precheck_type: 'uav-3d',
      precheck_version: 'uav-readiness-v1',
      precheck_score: readinessScore(value.precheck_score),
      precheck_purpose: clip(value.precheck_purpose, 120),
      precheck_evidence: clip(value.precheck_evidence, 600),
      precheck_recommendations: clip(value.precheck_recommendations, 400),
    };
  }

  function clearUavPrecheckHandoff() {
    try {
      window.sessionStorage.removeItem(UAV_HANDOFF_STORAGE_KEY);
    } catch (_error) {}
  }

  function readDashboardBriefHandoff(form, search) {
    const handoffSource = search.get('source');
    if (
      !form ||
      !['gcitypartners-dashboard', 'gtender', 'low-altitude-services'].includes(handoffSource)
    )
      return;
    if (search.get('intent') !== 'professional-review') return;
    if (search.get('dashboard_handoff') === 'skip') return;

    const storageKey = 'gcp_dashboard_brief_handoff_v1';
    let value;

    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return;
      value = JSON.parse(raw);
    } catch (_) {
      return;
    }

    const age = Date.now() - Number(value && value.created_at);
    const valid =
      value &&
      value.version === 1 &&
      value.source === handoffSource &&
      Number.isFinite(age) &&
      age >= 0 &&
      age <= 30 * 60 * 1000;

    if (!valid) {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (_) {}
      return;
    }

    const location =
      typeof value.project_location === 'string' ? value.project_location.trim().slice(0, 240) : '';
    const description =
      typeof value.description === 'string' ? value.description.trim().slice(0, 1200) : '';

    const locationField = form.elements.namedItem('project_location');
    const descriptionField = form.elements.namedItem('description');

    // Wait until the receiving fields actually exist.
    if (location && !locationField) return;
    if (description && !descriptionField) return;

    // Do not silently replace existing form or restored draft values.
    const locationConflict =
      location && clean(locationField.value) && clean(locationField.value) !== clean(location);

    const descriptionConflict =
      description &&
      clean(descriptionField.value) &&
      clean(descriptionField.value) !== clean(description);

    if (locationConflict || descriptionConflict) {
      setStatus(
        null,
        form,
        words(
          '当前需求已有内容，未覆盖。待带入资料暂时保留在此标签页。',
          '目前需求已有內容，未有覆蓋。待帶入資料暫時保留於此標籤頁。',
          '目前需求已有內容，未有覆蓋。待帶入資料暫時保留於此分頁。',
          'This brief already contains details. Nothing was overwritten; the incoming details remain temporarily stored in this tab.'
        )
      );
      return;
    }

    setField(form, 'project_location', location, false);
    setField(form, 'description', description, false);

    const importedWorkload = normalizeWorkloadEstimate(value.workload_estimate);
    if (importedWorkload) {
      workloadStateByForm.set(form, {
        estimate: importedWorkload,
        observation: clean(field(form, 'description')),
      });
    }

    const importedAssessments = normalizeCapabilityAssessments(value.capability_assessments);
    if (importedAssessments.length) {
      partnerStateByForm.set(form, {
        assessments: importedAssessments,
        observation: clean(field(form, 'description')),
      });
    } else {
      partnerStateByForm.delete(form);
    }
    renderPartnerSummary(form, importedAssessments, false);

    if (value.low_altitude_preparation) {
      form.setAttribute('data-gcp-low-altitude-imported', 'true');
    }

    const tenderContext = value.tender_context;
    const keepTenderContext =
      handoffSource === 'gtender' &&
      tenderContext &&
      typeof tenderContext.id === 'string' &&
      /^[A-Za-z0-9._:-]{3,160}$/.test(tenderContext.id);

    try {
      if (value.low_altitude_preparation || keepTenderContext) {
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            ...value,
            imported_at: Date.now(),
          })
        );
      } else {
        window.sessionStorage.removeItem(storageKey);
      }
    } catch (_) {}

    setStatus(
      null,
      form,
      words(
        '已带入需求资料，请补充并确认后继续。填写地点不代表地址已验证。',
        '已帶入需求資料，請補充並確認後繼續。填寫地點不代表地址已驗證。',
        '已帶入需求資料，請補充並確認後繼續。填寫地點不代表地址已驗證。',
        'Brief details imported. Review and complete them before continuing. An entered location is not a verified address.'
      )
    );
  }

  // Product Intake Composer transfers only a same-tab private draft by opaque ID.
  // This reader never treats the URL as a submission, entitlement or trusted product.
  function readProductIntakeHandoff(root, form, search) {
    var briefId = opaque(param(search, 'brief_id'));
    var expectedHandle = opaque(param(search, 'product_handle'));
    if (
      !/^gcp_[a-zA-Z0-9_-]{12,100}$/.test(briefId) ||
      !/^[a-z0-9][a-z0-9-]{1,119}$/.test(expectedHandle)
    )
      return false;

    var storage = readableStorage('sessionStorage');
    if (!storage) return false;
    var key = 'gcitypartners_private_intake:' + briefId;
    var value = parse(storageValue(storage, key), null);
    if (
      !value ||
      value.schema_version !== 'gcitypartners_private_intake_draft_v1' ||
      value.brief_id !== briefId ||
      value.product_handle !== expectedHandle ||
      value.formal_submission_status !== 'not_submitted' ||
      value.draft_status !== 'session_private'
    )
      return false;

    var created = Date.parse(value.created_at || '');
    var age = Date.now() - created;
    if (!Number.isFinite(age) || age < 0 || age > 30 * 60 * 1000) {
      try {
        storage.removeItem(key);
      } catch (_error) {}
      return false;
    }

    var sourceLane = laneValue(value.service_lane);
    // Advisory provenance only. The writer verified membership in product.collections.
    // Never use a stored collection handle for access or entitlement decisions.
    var sourceCollection = clean(value.source_collection);
    if (!/^[a-z0-9][a-z0-9-]{1,119}$/.test(sourceCollection)) sourceCollection = '';
    var allowedLanes = [
      'urban-renewal',
      'land-natural-resources',
      'property-assets-investment',
      'infrastructure-engineering',
      'digital-city',
      'water-resilience',
      'green-city-service',
      'industry-park-development',
    ];
    if (!sourceLane || allowedLanes.indexOf(sourceLane) < 0) return false;

    var location = clip(value.location, 240);
    var projectName = clip(value.project_name, 180);
    var goal = clip(value.decision_goal, 900);
    var outputLabels = {
      professional_review: 'Professional review',
      report: 'Report',
      dashboard: 'Dashboard',
      download: 'Downloadable output',
    };
    var outcome = Object.prototype.hasOwnProperty.call(outputLabels, value.expected_output)
      ? outputLabels[value.expected_output]
      : '';
    var observation = clip(
      [projectName ? 'Project / site: ' + projectName : '', goal ? 'Decision goal: ' + goal : '']
        .filter(Boolean)
        .join('\n'),
      1200
    );

    // Never replace an existing project draft, URL-based mission or populated Brief.
    if (
      field(form, 'project_location') ||
      field(form, 'description') ||
      field(form, 'desired_outcome') ||
      field(form, 'record_id') ||
      field(form, 'result_id') ||
      field(form, 'source_context')
    ) {
      setStatus(
        root,
        form,
        words(
          '当前需求已有内容，未覆盖。原私人草稿仍保留在本标签页。',
          '目前需求已有內容，未有覆蓋。原私人草稿仍保留在此分頁。',
          '目前需求已有內容，未有覆蓋。原私人草稿仍保留在此分頁。',
          'This brief already contains details. The incoming private draft was not overwritten or removed.'
        )
      );
      return false;
    }

    if (!location || !goal || !projectName) return false;
    setField(form, 'project_location', location, false);
    setField(form, 'description', observation, false);
    setField(form, 'desired_outcome', outcome, false);
    setField(
      form,
      'source_context',
      clip(
        'product-intake:' +
          expectedHandle +
          (sourceCollection ? '|collection:' + sourceCollection : ''),
        120
      ),
      false
    );
    setField(form, 'source', 'service-product-intake', false);
    if (sourceLane) {
      setField(form, 'service_lane', sourceLane, true);
      setField(form, 'business_need', businessNeedFor(sourceLane), true);
    }
    root.setAttribute('data-review-brief-state', 'private-intake-imported');
    root.setAttribute('data-review-brief-import-product', expectedHandle);
    try {
      storage.removeItem(key);
      if (storage.getItem('gcitypartners_private_intake_latest') === briefId)
        storage.removeItem('gcitypartners_private_intake_latest');
    } catch (_error) {}
    setStatus(
      root,
      form,
      words(
        '已带入本标签页私人草稿；尚未提交，请核对资料后再保存。',
        '已帶入此分頁私人草稿；尚未提交，請核對資料後再儲存。',
        '已帶入此分頁私人草稿；尚未送出，請核對資料後再儲存。',
        'Private draft imported into this brief. Nothing was submitted. Review and save when ready.'
      )
    );
    return true;
  }

  function requestContext(form) {
    var search = searchParams();
    var recordId = opaque(param(search, 'record_id'));
    var resultId = opaque(param(search, 'result_id'));
    var lookupId = recordId || resultId;
    var card = findCard(lookupId);

    var resourceHandoff = readResourceSharingHandoff();

    var uavPrecheckHandoff = readUavPrecheckHandoff(search);

    var lane = firstExisting(
      uavPrecheckHandoff && uavPrecheckHandoff.service_lane,
      param(search, 'service_lane'),
      param(search, 'scene'),
      param(search, 'scenario'),
      field(form, 'service_lane'),
      card &&
        firstExisting(
          card.service_lane,
          card.next_step_service_lane,
          card.scene,
          card.scene_handle,
          card.scene_key
        ),
      'urban-renewal'
    );

    lane = laneValue(lane) || 'urban-renewal';

    setField(form, 'source', param(search, 'source'), true);

    // A draft product routes through this existing Brief; carry only its allowlisted
    // product identity as advisory source context, never as entitlement or approval.
    var ruralProductHandle = firstExisting(param(search, 'product'), param(search, 'service'));
    if (
      [
        'smartvillage-aiot-platform',
        'agricultural-land-yield-analysis',
        'facility-agricultural-land-filing',
      ].indexOf(ruralProductHandle) !== -1
    ) {
      setField(form, 'source_context', 'rural-product:' + ruralProductHandle, false);
    }

    setField(form, 'record_id', recordId);

    setField(form, 'result_id', resultId);

    setField(form, 'service_lane', lane);

    setField(
      form,
      'project_location',
      card && firstExisting(card.place_label, card.location, card.district_label, card.district)
    );

    setField(form, 'business_need', businessNeedFor(lane));

    setField(form, 'description', cardDescription(card));

    if (uavPrecheckHandoff) {
      setField(form, 'source', uavPrecheckHandoff.source, true);
      setField(form, 'source_context', uavPrecheckHandoff.source_context, true);
      setField(form, 'source_card_type', uavPrecheckHandoff.source_card_type, true);
      setField(form, 'service_lane', uavPrecheckHandoff.service_lane, true);
      setField(form, 'business_need', uavPrecheckHandoff.business_need, true);
      setField(form, 'project_stage', uavPrecheckHandoff.project_stage, true);
      setField(form, 'precheck_type', uavPrecheckHandoff.precheck_type, true);
      setField(form, 'precheck_version', uavPrecheckHandoff.precheck_version, true);
      setField(form, 'precheck_score', uavPrecheckHandoff.precheck_score, true);
      setField(form, 'precheck_purpose', uavPrecheckHandoff.precheck_purpose, true);
      setField(form, 'precheck_evidence', uavPrecheckHandoff.precheck_evidence, true);
      setField(form, 'precheck_recommendations', uavPrecheckHandoff.precheck_recommendations, true);
    }

    if (resourceHandoff) {
      setField(form, 'source', 'resource-sharing-check', true);

      setField(form, 'source_context', 'resource-sharing', true);

      setField(form, 'source_card_type', 'resource_sharing', true);

      setField(form, 'resource_mode', resourceHandoff.resource_mode, true);

      setField(form, 'resource_type', resourceHandoff.resource_type, true);

      setField(
        form,
        'resource_goals',
        Array.isArray(resourceHandoff.resource_goals)
          ? resourceHandoff.resource_goals.join('|')
          : '',
        true
      );

      setField(form, 'project_location', resourceHandoff.resource_region, false);

      setField(form, 'description', resourceHandoff.resource_brief, false);

      setField(
        form,
        'desired_outcome',
        Array.isArray(resourceHandoff.resource_goal_labels)
          ? resourceHandoff.resource_goal_labels.join(' · ')
          : '',
        false
      );

      setField(form, 'business_need', 'enterprise-brief', true);
    }

    return {
      search: search,
      recordId: recordId,
      resultId: resultId,
      lookupId: lookupId,
      card: card,
      lane: lane,
      precheckHandoff: uavPrecheckHandoff,
    };
  }

  function precheckEvidenceRows(value) {
    return clip(value, 600)
      .split('|')
      .map(function (row) {
        var parts = row.split(':');
        return {
          key: clip(parts[0], 80),
          state: clip(parts[1], 40),
        };
      })
      .filter(function (row) {
        return row.key && row.state;
      });
  }

  function precheckPurposeLabel(value) {
    var key = clip(value, 120);
    if (key === 'condition_record') {
      return words('记录现场状况', '記錄現場狀況', '記錄現場狀況', 'Document site conditions');
    }
    if (key === '3d_modeling') {
      return words('建立 3D 模型', '建立 3D 模型', '建立 3D 模型', 'Build a 3D model');
    }
    if (key === 'survey_analysis') {
      return words(
        '量测土方与比较进度',
        '量測土方與比較進度',
        '量測土方與比較進度',
        'Measure volume and compare progress'
      );
    }
    return key || '—';
  }

  function precheckEvidenceLabel(value) {
    var key = clip(value, 80);
    if (key === 'aerial_imagery') {
      return words('航拍影像与照片', '航拍影像與照片', '航拍影像與照片', 'Aerial imagery');
    }
    if (key === 'site_boundary') {
      return words(
        '场地边界（CAD / KML）',
        '場地邊界（CAD / KML）',
        '場地邊界（CAD / KML）',
        'Site boundary (CAD / KML)'
      );
    }
    if (key === 'control_points') {
      return words(
        '控制点与坐标系统',
        '控制點與坐標系統',
        '控制點與坐標系統',
        'Control points and coordinates'
      );
    }
    return key.replace(/_/g, ' ');
  }

  function precheckStateLabel(value) {
    var key = clip(value, 40);
    if (key === 'have') {
      return words('已齐备', '已齊備', '已齊備', 'Ready');
    }
    if (key === 'unsure') {
      return words('待确认', '待確認', '待確認', 'Unsure');
    }
    if (key === 'missing') {
      return words('尚未取得', '尚未取得', '尚未取得', 'Missing');
    }
    return key;
  }

  function precheckRecommendationLabel(value) {
    return clip(value, 400)
      .split('|')
      .map(function (key) {
        return precheckEvidenceLabel(key);
      })
      .filter(Boolean)
      .join(' · ');
  }

  function renderPrecheckHandoff(root, form, draft) {
    if (!root || !form || !draft || draft.precheck_type !== 'uav-3d') return;

    var panel = root.querySelector('[data-gcp-precheck-import]');
    if (!panel) return;

    var score = panel.querySelector('[data-gcp-precheck-score]');
    var purpose = panel.querySelector('[data-gcp-precheck-purpose]');
    var evidence = panel.querySelector('[data-gcp-precheck-evidence]');
    var recommendations = panel.querySelector('[data-gcp-precheck-recommendations]');

    if (score) score.textContent = draft.precheck_score || '—';
    if (purpose) purpose.textContent = precheckPurposeLabel(draft.precheck_purpose);

    if (evidence) {
      evidence.textContent = '';
      precheckEvidenceRows(draft.precheck_evidence).forEach(function (row) {
        var item = document.createElement('li');
        item.setAttribute('data-state', row.state);
        item.textContent = precheckEvidenceLabel(row.key) + ' · ' + precheckStateLabel(row.state);
        evidence.appendChild(item);
      });
    }

    if (recommendations) {
      recommendations.textContent =
        precheckRecommendationLabel(draft.precheck_recommendations) ||
        words('没有明显缺项', '沒有明顯缺項', '沒有明顯缺項', 'No obvious gap');
    }

    panel.hidden = false;
    root.setAttribute('data-precheck-handoff', 'uav-3d');
    var briefSurface = root.querySelector('[data-gcp-contextual-brief]');
    if (briefSurface) briefSurface.setAttribute('data-precheck-handoff', 'uav-3d');
    root.setAttribute('data-gcp-funnel-stage', 'project-details');

    var contextPanel = root.querySelector('[data-gcp-review-context]');
    if (contextPanel) contextPanel.hidden = true;

    var kicker = root.querySelector('[data-gcp-review-kicker]');
    var title = root.querySelector('[data-gcp-review-title]');
    var desc = root.querySelector('[data-gcp-review-desc]');
    if (kicker) kicker.textContent = words('正式项目', '正式項目', '正式專案', 'PROJECT BRIEF');
    if (title)
      title.textContent = words(
        '补充项目资料',
        '補充項目資料',
        '補充專案資料',
        'Complete project details'
      );
    if (desc)
      desc.textContent = words(
        '结果已带入。补充图资链接、精度、时间及预算即可。',
        '結果已帶入。補充圖資連結、精度、時間及預算即可。',
        '結果已帶入。補充圖資連結、精度、時間及預算即可。',
        'Your result is already here. Add your data link, accuracy, timeline and budget.'
      );

    Array.prototype.forEach.call(
      form.querySelectorAll('[data-gcp-precheck-duplicate]'),
      function (node) {
        node.hidden = true;
        Array.prototype.forEach.call(
          node.querySelectorAll('input,select,textarea'),
          function (field) {
            field.required = false;
            field.disabled = false;
          }
        );
      }
    );
  }

  function restore(root, form, context) {
    var draft = findDraftForContext(context.recordId, context.resultId);
    if (!draft) return false;

    setField(form, 'record_id', draft.record_id, true);
    setField(form, 'result_id', draft.result_id, true);
    setField(form, 'service_lane', draft.service_lane, true);
    setField(form, 'project_location', draft.project_location, true);
    setField(form, 'project_stage', draft.project_stage, true);
    setField(form, 'business_need', draft.business_need, true);

    setField(form, 'description', draft.observation, true);

    const restoredWorkload = normalizeWorkloadEstimate(draft.workload_estimate);
    if (restoredWorkload) {
      workloadStateByForm.set(form, {
        estimate: restoredWorkload,
        observation: clean(field(form, 'description')),
      });
    } else {
      workloadStateByForm.delete(form);
    }

    const restoredAssessments = normalizeCapabilityAssessments(draft.capability_assessments);
    if (restoredAssessments.length) {
      partnerStateByForm.set(form, {
        assessments: restoredAssessments,
        observation: clean(field(form, 'description')),
      });
    } else {
      partnerStateByForm.delete(form);
    }
    renderPartnerSummary(form, restoredAssessments, false);

    setField(form, 'desired_outcome', draft.desired_outcome, true);

    setField(form, 'evidence_files', draft.evidence_files, true);

    [
      ['precheck_type', draft.precheck_type],
      ['precheck_version', draft.precheck_version],
      ['precheck_score', draft.precheck_score],
      ['precheck_purpose', draft.precheck_purpose],
      ['precheck_evidence', draft.precheck_evidence],
      ['precheck_recommendations', draft.precheck_recommendations],
      ['asset_access_link', draft.asset_access_link],
      ['gsd_requirement', draft.gsd_requirement],
      ['project_timeline', draft.project_timeline],
      ['budget_range', draft.budget_range],
    ].forEach(function (entry) {
      setField(form, entry[0], entry[1], true);
    });

    renderPrecheckHandoff(root, form, draft);

    setField(form, 'source_context', draft.source_context, true);

    setField(form, 'source_card_id', draft.source_card_id, true);

    setField(form, 'source_card_type', draft.source_card_type, true);

    setField(form, 'resource_mode', draft.resource_mode, true);

    setField(form, 'resource_type', draft.resource_type, true);

    setField(form, 'resource_goals', draft.resource_goals, true);

    context.recordId = draft.record_id;
    context.resultId = draft.result_id;
    context.lane = draft.service_lane;
    root.setAttribute('data-review-brief-state', 'local-draft-restored');
    root.setAttribute('data-review-brief-record-id', draft.record_id);
    ensureContactLink(form, draft);
    setStatus(
      root,
      form,
      words(
        '已载入草稿，尚未发送需求。',
        '已載入草稿，尚未發送需求。',
        '已載入草稿，尚未送出需求。',
        'Your draft is restored. No request has been sent.'
      )
    );
    return true;
  }

  function buildDraft(form, context) {
    var selectedNeed = field(form, 'business_need');
    var selectedLane = laneForNeed(selectedNeed, field(form, 'service_lane') || context.lane);
    var observation = clip(field(form, 'description'), 1200);
    var desiredOutcome = clip(field(form, 'desired_outcome'), 800);
    var recordId =
      opaque(field(form, 'record_id')) ||
      context.recordId ||
      'gcp-draft-' +
        Date.now() +
        '-' +
        stableHash(
          [
            field(form, 'project_location'),
            field(form, 'project_stage'),
            selectedLane,
            observation,
            desiredOutcome,
          ].join('|')
        );
    var resultId = opaque(field(form, 'result_id'));
    if (resultId === recordId) resultId = '';

    return normalize({
      source: firstExisting(
        field(form, 'source'),
        param(context.search, 'source'),
        'gcitypartners-gateway'
      ),
      lead_type: firstExisting(param(context.search, 'lead_type'), 'city_issue'),
      record_id: recordId,
      result_id: resultId,
      scene: firstExisting(
        param(context.search, 'scene'),
        param(context.search, 'scenario'),
        selectedLane
      ),
      service_lane: selectedLane,
      business_line: selectedNeed,
      business_line_label: optionLabel(form.elements && form.elements.namedItem('business_need')),
      business_line_canonical_lane: selectedLane,
      project_location: field(form, 'project_location'),
      project_stage: field(form, 'project_stage'),
      business_need: selectedNeed,
      observation: observation,
      workload_estimate: workloadForForm(form),
      capability_assessments: partnerAssessmentsForForm(form),
      desired_outcome: desiredOutcome,
      evidence_files: field(form, 'evidence_files'),
      precheck_type: field(form, 'precheck_type'),
      precheck_version: field(form, 'precheck_version'),
      precheck_score: field(form, 'precheck_score'),
      precheck_purpose: field(form, 'precheck_purpose'),
      precheck_evidence: field(form, 'precheck_evidence'),
      precheck_recommendations: field(form, 'precheck_recommendations'),
      asset_access_link: field(form, 'asset_access_link'),
      gsd_requirement: field(form, 'gsd_requirement'),
      project_timeline: field(form, 'project_timeline'),
      budget_range: field(form, 'budget_range'),
      csdi_location_name: field(form, 'csdi_location_name'),
      csdi_location_address: field(form, 'csdi_location_address'),
      csdi_location_district: field(form, 'csdi_location_district'),
      csdi_location_x: field(form, 'csdi_location_x'),
      csdi_location_y: field(form, 'csdi_location_y'),
      csdi_source: field(form, 'csdi_source'),
      source_context: firstExisting(
        field(form, 'source_context'),
        context.card ? 'local-city-memory-card' : 'manual-or-safe-url-context'
      ),

      source_card_id: firstExisting(
        field(form, 'source_card_id'),
        context.card ? cardId(context.card) : ''
      ),

      source_card_type: firstExisting(
        field(form, 'source_card_type'),
        context.card ? 'city_memory_card' : 'manual_city_issue'
      ),

      resource_mode: field(form, 'resource_mode'),

      resource_type: field(form, 'resource_type'),

      resource_goals: field(form, 'resource_goals'),

      card_context_found: Boolean(context.card),
      card_context_source: context.card ? context.card.__source_key : 'none',
    });
  }

  function syncLowAltitudeFlow(form, draft) {
    if (!form || !draft || !form.hasAttribute('data-gcp-low-altitude-imported')) return;

    const key = 'gcp_dashboard_brief_handoff_v1';
    const source = searchParams().get('source');

    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) throw new Error('missing_handoff');
      const value = JSON.parse(raw);
      const age = Date.now() - Number(value && value.created_at);

      if (
        !value ||
        value.version !== 1 ||
        !value.low_altitude_preparation ||
        !['gtender', 'low-altitude-services'].includes(source) ||
        value.source !== source ||
        !Number.isFinite(age) ||
        age < 0 ||
        age > 30 * 60 * 1000 ||
        (value.brief_record_id && value.brief_record_id !== draft.record_id)
      )
        throw new Error('handoff_conflict');

      if (
        source === 'gtender' &&
        (!value.tender_context ||
          !/^[A-Za-z0-9._:-]{3,240}$/.test(String(value.tender_context.id || '')))
      )
        throw new Error('invalid_tender');

      const returnUrl = new URL(
        localizedPagePath('/pages/low-altitude-economy'),
        window.location.origin
      );
      returnUrl.searchParams.set('source', source);
      returnUrl.searchParams.set('intent', 'professional-review');
      if (source === 'gtender') {
        returnUrl.searchParams.set('tender', value.tender_context.id);
      }

      const actions = form.querySelector('.gcp-actions');
      if (!actions) throw new Error('missing_actions');

      window.sessionStorage.setItem(
        key,
        JSON.stringify({
          ...value,
          created_at: Date.now(),
          brief_record_id: draft.record_id,
          project_location: draft.project_location || '',
          description: draft.observation || '',
          workload_estimate: normalizeWorkloadEstimate(draft.workload_estimate),
          capability_assessments: normalizeCapabilityAssessments(draft.capability_assessments),
          capability_gaps: capabilityGapsFromAssessments(draft.capability_assessments),
          partner_requests: partnerRequestsFromAssessments(draft.capability_assessments),
          brief_saved_at: Date.now(),
        })
      );

      let link = actions.querySelector('[data-gcp-low-altitude-return]');
      if (!link) {
        link = document.createElement('a');
        link.className = 'gcp-btn gcp-btn--secondary';
        link.setAttribute('data-gcp-low-altitude-return', '');
        link.setAttribute('data-no-legacy-normalize', 'true');
        actions.appendChild(link);
      }

      link.href = returnUrl.href;
      link.textContent = words(
        '返回低空准备',
        '返回低空準備',
        '返回低空準備',
        'Return to low-altitude preparation'
      );
      link.hidden = false;

      link.onclick = (event) => {
        const savedLocation = String(draft.project_location || '').trim();
        const savedDescription = String(draft.observation || '').trim();
        const currentLocation = field(form, 'project_location');
        const currentDescription = field(form, 'description');

        if (currentLocation !== savedLocation || currentDescription !== savedDescription) {
          event.preventDefault();
          setStatus(
            null,
            form,
            words(
              '地点或需求内容有未保存修改，请先更新草稿再返回。',
              '地點或需求內容有未儲存修改，請先更新草稿再返回。',
              '地點或需求內容有未儲存修改，請先更新草稿再返回。',
              'Location or brief details have unsaved changes. Update the draft before returning.'
            )
          );
        }
      };
    } catch (_) {
      setStatus(
        null,
        form,
        words(
          '专业草稿已保存，但低空返回资料未能同步。请保留本页的草稿链接。',
          '專業草稿已儲存，但低空返回資料未能同步。請保留本頁的草稿連結。',
          '專業草稿已儲存，但低空返回資料未能同步。請保留本頁的草稿連結。',
          'The professional draft was saved, but return context could not be synchronised. Keep this page’s draft URL.'
        )
      );
    }
  }

  function dispatchSaved(draft, storage) {
    try {
      window.dispatchEvent(
        new CustomEvent('gcitypartners:review-brief:saved', {
          detail: {
            record_id: draft.record_id,
            result_id: draft.result_id,
            service_lane: draft.service_lane,
            precheck_type: draft.precheck_type,
            source_context: draft.source_context,
            storage: storage,
            formal_submission_status: 'not_submitted',
          },
        })
      );
    } catch (_error) {}
  }

  function addListener(target, type, listener, state, options) {
    if (!target) return;
    var listenerOptions = state.controller
      ? Object.assign({}, options || {}, { signal: state.controller.signal })
      : options;

    try {
      target.addEventListener(type, listener, listenerOptions);
    } catch (_error) {
      target.addEventListener(type, listener, options);
      state.cleanups.push(function () {
        target.removeEventListener(type, listener, options);
      });
    }
  }

  function bindForm(root, form, state) {
    if (!root || !form || form.getAttribute('data-gcp-safe-draft-bound') === VERSION) return;

    form.setAttribute('data-gcp-safe-draft-bound', VERSION);
    root.setAttribute('data-review-brief-owner', 'assets/gcitypartners-review-brief-schema.js');
    root.setAttribute('data-review-brief-storage', STORAGE_KEY);
    root.setAttribute(
      'data-review-brief-submit-mode',
      root.hasAttribute('data-review-brief-project-endpoint')
        ? 'local-draft-plus-explicit-project'
        : 'local-draft-only'
    );

    const locationField = form.elements.namedItem('project_location');
    const locationStatus = form.querySelector('[data-gcp-location-status]');

    function syncLocationStatus() {
      if (!locationField || !locationStatus) return;

      const hasLocation = Boolean(clean(locationField.value));

      locationStatus.setAttribute(
        'data-gcp-brief-location-state',
        hasLocation ? 'unverified' : 'not-entered'
      );

      locationStatus.textContent = hasLocation
        ? words(
            '地址尚未核实，可随草稿保存。',
            '地址尚未核實，可隨草稿儲存。',
            '地址尚未核實，可隨草稿儲存。',
            'Address not verified. You can save it with your draft.'
          )
        : words(
            '地址可随草稿保存，保存不代表已核实。',
            '地址可隨草稿儲存，儲存不代表已核實。',
            '地址可隨草稿儲存，儲存不代表已核實。',
            'The address can be saved with your draft. Saving does not verify it.'
          );
    }

    addListener(locationField, 'input', syncLocationStatus, state);
    addListener(locationField, 'change', syncLocationStatus, state);

    var submit = form.querySelector('[data-enterprise-triage-submit]');
    var context = requestContext(form);
    bridgeLegacyHash(form, state);
    var restored = restore(root, form, context);

    if (!restored && context.precheckHandoff) {
      renderPrecheckHandoff(
        root,
        form,
        normalize(
          Object.assign({}, context.precheckHandoff, {
            record_id: '',
            source: 'uav-precheck',
            service_lane: context.precheckHandoff.service_lane || context.lane,
          })
        )
      );
    }

    var restoredDraft = restored ? findDraftForContext(context.recordId, context.resultId) : null;
    var importedUav = Boolean(
      (restoredDraft && restoredDraft.precheck_type === 'uav-3d') || context.precheckHandoff
    );

    if (param(searchParams(), 'precheck') === 'uav-3d' && !importedUav) {
      setStatus(
        root,
        form,
        words(
          '结果未能载入或已过期。请返回重新检查。',
          '結果未能載入或已過期。請返回重新檢查。',
          '結果未能載入或已過期。請返回重新檢查。',
          'Your result is unavailable or has expired. Return to the check and try again.'
        )
      );
    }

    if (submit) {
      submit.textContent = importedUav
        ? words(
            '继续填写联系资料 →',
            '繼續填寫聯絡資料 →',
            '繼續填寫聯絡資料 →',
            'Continue to contact details →'
          )
        : restored
          ? words('更新草稿', '更新草稿', '更新草稿', 'Update draft')
          : words('保存草稿', '保存草稿', '儲存草稿', 'Save draft');
    }

    var need = form.querySelector('[name="business_need"]');
    addListener(
      need,
      'change',
      function () {
        if (!submit || field(form, 'precheck_type') === 'uav-3d') return;
        var checkEngine = window.GCityPartnersCheckEngine;
        if (checkEngine && checkEngine.checkTypes.indexOf(field(form, 'business_need')) !== -1)
          return;
        submit.textContent = context.recordId
          ? words('更新草稿', '更新草稿', '更新草稿', 'Update draft')
          : words('保存草稿', '保存草稿', '儲存草稿', 'Save draft');
      },
      state
    );

    if (need && typeof need.dispatchEvent === 'function') {
      need.dispatchEvent(new window.Event('change', { bubbles: true }));
    }

    // Import only after existing context and saved draft restoration.
    // The handoff reader preserves existing values when they conflict.
    readDashboardBriefHandoff(form, searchParams());
    readProductIntakeHandoff(root, form, searchParams());
    syncLocationStatus();

    addListener(
      form,
      'submit',
      function (event) {
        event.preventDefault();

        if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

        var draft;
        var result;
        try {
          draft = buildDraft(form, context);
          result = save(draft);
          draft = result.draft;
        } catch (_error) {
          root.setAttribute('data-review-brief-state', 'save-failed');
          setStatus(
            root,
            form,
            words(
              '未能保存草稿。请保留此页，再试一次。',
              '未能儲存草稿。請保留此頁，再試一次。',
              '未能儲存草稿。請保留此頁，再試一次。',
              'Could not save your draft. Keep this page open and try again.'
            )
          );
          return;
        }

        context.recordId = draft.record_id;
        context.resultId = draft.result_id;
        context.lane = draft.service_lane;
        setField(form, 'record_id', draft.record_id, true);
        setField(form, 'service_lane', draft.service_lane, true);
        writeRecordIdToUrl(draft.record_id);
        root.setAttribute('data-review-brief-state', 'local-draft-saved');
        root.setAttribute('data-review-brief-record-id', draft.record_id);
        root.setAttribute('data-review-brief-storage-owner', result.storage);
        if (submit) {
          submit.textContent =
            draft.precheck_type === 'uav-3d'
              ? words('更新项目资料', '更新項目資料', '更新專案資料', 'Update project details')
              : words('更新草稿', '更新草稿', '更新草稿', 'Update draft');
        }
        ensureContactLink(form, draft);
        const alreadyCreatedProject = Boolean(form.dataset.gcpProjectJobId);
        setStatus(
          root,
          form,
          alreadyCreatedProject
            ? words(
                'Project 已建立。此草稿的最新內容尚未同步到該 Project。',
                'Project 已建立。此草稿的最新內容尚未同步到該 Project。',
                'Project 已建立。此草稿的最新內容尚未同步到該 Project。',
                'Project already created. Draft changes are not synchronized to that Project.'
              )
            : draft.precheck_type === 'uav-3d'
              ? words(
                  '草稿已保存。到联系表单补充公司与联系资料，再发送需求。',
                  '草稿已儲存。到聯絡表單補充公司與聯絡資料，再發送需求。',
                  '草稿已儲存。到聯絡表單補充公司與聯絡資料，再送出需求。',
                  'Draft saved. Add company and contact details in the contact form, then send your request.'
                )
              : words(
                  '草稿已保存，尚未发送需求。准备好后，可继续填写联系资料。',
                  '草稿已儲存，尚未發送需求。準備好後，可繼續填寫聯絡資料。',
                  '草稿已儲存，尚未送出需求。準備好後，可繼續填寫聯絡資料。',
                  'Draft saved. No request has been sent. Continue to the contact form when you are ready.'
                )
        );
        if (draft.precheck_type === 'uav-3d') clearUavPrecheckHandoff();
        const partnerState = partnerStateByForm.get(form);
        const partnerAssessmentChanged = Boolean(
          partnerState && partnerState.observation !== clean(field(form, 'description'))
        );
        renderPartnerSummary(form, draft.capability_assessments, partnerAssessmentChanged);
        dispatchSaved(draft, result.storage);
        syncLowAltitudeFlow(form, draft);
      },
      state,
      true
    );
  }

  function mount(root) {
    if (!root || stateByRoot.has(root)) return;

    var form = root.querySelector(FORM_SELECTOR);
    if (!form) return;

    var state = {
      active: true,
      controller: typeof AbortController === 'function' ? new AbortController() : null,
      cleanups: [],
      scrollTimer: null,
    };
    stateByRoot.set(root, state);
    mountedRoots.add(root);
    bindForm(root, form, state);
  }

  function destroy(root) {
    var state = root ? stateByRoot.get(root) : null;
    if (!state) return;

    state.active = false;
    if (state.controller) state.controller.abort();
    state.cleanups.forEach(function (cleanup) {
      cleanup();
    });
    if (state.scrollTimer != null) window.clearTimeout(state.scrollTimer);

    var form = root.querySelector(FORM_SELECTOR);
    if (form) form.removeAttribute('data-gcp-safe-draft-bound');

    mountedRoots.delete(root);
    stateByRoot.delete(root);
    root.removeAttribute('data-review-brief-owner');
    root.removeAttribute('data-review-brief-storage');
    root.removeAttribute('data-review-brief-submit-mode');
  }

  function bind(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) mount(target);
    if (!target.querySelectorAll) return;
    Array.prototype.forEach.call(target.querySelectorAll(ROOT_SELECTOR), mount);
  }

  function destroyAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) destroy(target);
    if (!target.querySelectorAll) return;
    Array.prototype.forEach.call(target.querySelectorAll(ROOT_SELECTOR), destroy);
  }

  window.GCityPartnersReviewBriefSchema = Object.freeze({
    version: VERSION,
    storageKey: STORAGE_KEY,
    fields: FIELDS.slice(),
    owner: 'assets/gcitypartners-review-brief-schema.js',
    boundary: Object.freeze({
      draft_only: true,
      contact_not_persisted: true,
      no_pii_or_observation_in_url: true,
      record_id_result_id_separated: true,
      explicit_contact_handoff_only: true,
      no_formal_professional_conclusion: true,
      no_checkout: true,
      no_file_upload: true,
      precheck_handoff_supported: true,
      commercial_context_local_draft_only: true,
      no_backend_submission: true,
    }),
    normalize: normalize,
    save: save,
    findDraft: findDraft,
    bind: bind,
    destroy: destroyAll,
  });

  document.addEventListener('shopify:section:load', function (event) {
    bind(event.target || document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        bind(document);
      },
      { once: true }
    );
  } else {
    bind(document);
  }
})(window, document);
